import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import {
  AdditionalFieldTypeEnum,
  EntityTypeEnum,
  State,
  WorkflowPossibleTransitionOutDTO,
  WorkflowTransitionAdditionalFieldInDTO,
} from '@api/ringil-api.v2';
import { isNotNullOrUndefined, isNullOrUndefined, visualiseWorkflowStateState } from '@utils/utils';
import SvgTagEdit from '@components/ringil3/icons/TagEdit';
import useForm from '@hooks/useForm/useForm';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { MultiSelect } from 'primereact/multiselect';
import MultipleValues from '@components/ringil3/HelperComponents/MultipleValues.tsx';
import { resolveStateIcon } from '@utils/reservation.ts';
import { SecuredConfig } from '@components/security/securedDecoration';

interface ComponentProps {
  state: State;
  entityId: string;
  entityType: EntityTypeEnum;
  onReload: () => void;
  readOnly?: boolean;
  secured?: SecuredConfig;
}

interface Form {
  state: string;
  additionalField?: WorkflowTransitionAdditionalFieldInDTO;
}

const WorkflowStateTag: React.FC<ComponentProps> = ({ state, entityId, entityType, onReload, readOnly, secured }) => {
  const { tr } = useTranslationLgs();
  const { getWorkflowPossibleTransition, performTransitionOnEntity } = useContext(ApiContext);
  const [workflowInfo, setWorkflowInfo] = useState<WorkflowPossibleTransitionOutDTO>();

  const form = useForm<Form>(
    yup.object().shape({}),
    {
      state: state?.name,
      additionalField: isNotNullOrUndefined(state.additionalField)
        ? {
          additionalFieldValue: state.additionalField?.additionalFieldValue,
          additionalFieldName: state.additionalField?.additionalFieldName,
        }
        : null,
    },
    v => save(v.state),
    true,
    false,
  );

  useEffect(() => {
    // The possible-transitions EP requires change permission; skip it for a read-only viewer
    // (e.g. a partner who may see but not change the status) so it does not 403.
    if (readOnly) return;
    const c = new AbortController();
    getWorkflowPossibleTransition(entityId, entityType, d => {
      if (c.signal.aborted) return;
      setWorkflowInfo(d);
    });
    return () => {
      c.abort();
    };
  }, [state, readOnly]);

  const renderIcon = () => {
    if (state.icon === null) return <></>;
    return resolveStateIcon(state.icon);
  };

  const resolveText = () => {
    const stateLabel = tr(`CreateEditWorkflowStateComponent.stateName${state.name}`, `${state.name}`);
    const reasons = state.additionalField?.additionalFieldValue ?? [];
    if (isNullOrUndefined(state.additionalField) || reasons.length === 0) {
      return stateLabel;
    }
    return (
      <MultipleValues
        values={[
          stateLabel,
          ...reasons.map(v =>
            tr(`ReservationWorkflowStateTag.additionalField.${state.additionalField.additionalFieldName}.${v}`, `${v}`),
          ),
        ]}
      />
    );
  };

  // Read-only viewers (e.g. a partner allowed to see but not change status) get the same tag
  // without the edit action and without fetching the possible transitions.
  if (readOnly) {
    return <ButtonTag label={tr(`WorkflowStateTag.state`, `State`)} value={resolveText()} icon={renderIcon()} readonly={true} secured={secured} />;
  }

  if (isNullOrUndefined(workflowInfo)) return <></>;

  const save = (state: string) => {
    performTransitionOnEntity(
      entityId,
      entityType,
      {
        transitionToName: state,
        additionalField: isNotNullOrUndefined(workflowInfo?.possibleTransitions?.find(t => t.to.name === state)?.to.additionalField) ? form.values.additionalField : null,
      },
      () => {
        onReload();
      },
    );
  };

  const renderAdditionalField = (possibleState: string) => {
    const transition = workflowInfo?.possibleTransitions?.find(t => t.to.name === possibleState);
    const additionalField = transition?.to?.additionalField;

    if (isNullOrUndefined(additionalField)) return <></>;

    const setAdditionalFieldValue = (values: string[]) => {
      form.useProduce(d => {
        if (isNullOrUndefined(d.additionalField)) {
          d.additionalField = {
            additionalFieldName: additionalField.additionalFieldName,
            additionalFieldValue: values,
          };
        } else {
          d.additionalField.additionalFieldValue = values;
          d.additionalField.additionalFieldName = additionalField.additionalFieldName;
        }
      });
    };

    const itemLabel = (d: string) =>
      tr(`ReservationWorkflowStateTag.additionalField.${additionalField.additionalFieldName}.${d}`, `${d}`);

    const reasonOptions = (additionalField.additionalFieldAvailableValues ?? [])
      .map(v => ({ label: itemLabel(v), value: v }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

    if (additionalField.additionalFieldType === AdditionalFieldTypeEnum.LIST_ENUMS) {
      return (
        <Dropdown
          options={reasonOptions}
          optionLabel={'label'}
          optionValue={'value'}
          value={form.values.additionalField?.additionalFieldValue?.[0] ?? null}
          onChange={v => setAdditionalFieldValue(v.value ? [v.value] : [])}
          filter
          filterBy={'label'}
          placeholder={tr(`ReservationWorkflowStateTag.additionalFieldPlaceholder.${additionalField.additionalFieldName}`, `${additionalField.additionalFieldName}`)}
        />
      );
    }

    if (additionalField.additionalFieldType === AdditionalFieldTypeEnum.MULTI_LIST_ENUMS) {
      return (
        <MultiSelect
          options={reasonOptions}
          optionLabel={'label'}
          optionValue={'value'}
          value={form.values.additionalField?.additionalFieldValue ?? []}
          onChange={v => setAdditionalFieldValue(v.value ?? [])}
          filter
          filterBy={'label'}
          placeholder={tr(`ReservationWorkflowStateTag.additionalFieldPlaceholder.${additionalField.additionalFieldName}`, `${additionalField.additionalFieldName}`)}
          display="chip"
        />
      );
    }
  };

  const stateLabel = (s: State) => tr(`CreateEditWorkflowStateComponent.stateName${s.name}`, `${s.name}`);

  const pane = (
    <>
      <Dropdown
        options={[
          state,
          ...workflowInfo.possibleTransitions.map(t => t.to)
        ].filter((item, index, self) =>
          self.findIndex(i => i.name === item.name) === index
        ).sort((a, b) => stateLabel(a).localeCompare(stateLabel(b), undefined, { sensitivity: 'base' }))}
        optionLabel={'name'}
        optionValue={'name'}
        itemTemplate={d => visualiseWorkflowStateState(d, tr)}
        valueTemplate={d => visualiseWorkflowStateState(d, tr)}
        value={form.values.state}
        onChange={v => {
          form.setFieldValue('state', v.value);
        }}
        placeholder={workflowInfo.currentState}
      />
      {renderAdditionalField(form.values.state)}
    </>
  );

  return (
    <>
      <ButtonTag
        label={tr(`WorkflowStateTag.state`, `State`)}
        value={resolveText()}
        icon={renderIcon()}
        actionIcon={<SvgTagEdit />}
        actionPane={pane}
        onSave={() => form.validateAndSend()}
        hideButtons={false}
        autoClose={true}
        readonly={readOnly}
        secured={secured}
      />
    </>
  );
};

interface Props {
  reservationId: string;
  form: any;
}

export default WorkflowStateTag;
